import { useState, useEffect } from 'react';
import { STEDEN, REVIEWS } from './data.js';
import { IconStar, IconPhone, IconMail, IconCheck, IconCity, IconArrow, IconPin } from './icons.jsx';
import { Hero, Stats, Plagen, Werkwijze, USPs } from './home-top.jsx';
import { url } from './url.js';
import { useSEO, buildBreadcrumb } from './seo.jsx';
import { getGoogleAggregate } from './google-reviews.js';
import { submitForm } from './submit-form.js';

// Live Google-aggregaat hook (rating + totaal). Fallback uit data.js voor
// prerender, client-side upgrade via Cloudflare Worker → Google Places.
function useGoogleAggregate() {
  const [agg, setAgg] = useState({ rating: REVIEWS.rating, total: REVIEWS.total });
  useEffect(() => {
    let alive = true;
    getGoogleAggregate().then(data => {
      if (!alive || !data) return;
      if (data.rating !== agg.rating || data.total !== agg.total) {
        setAgg({ rating: data.rating, total: data.total });
      }
    });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return agg;
}

// ─── Reviews ──────────────────────────────────────────────────────────
function Reviews() {
  const agg = useGoogleAggregate();
  const ratingStr = String(agg.rating).replace('.', ',');
  const reviews = [
    { avBg: "#FF6B2B", initial: "E", name: "Ellen V.", loc: "Leiden",
      text: "Ontzettend snel en professioneel geholpen! Hadden last van muizen en binnen een dag was het team aanwezig. Vakkundig werk en daarna nooit meer problemen gehad.",
      date: "2 weken geleden", stars: 5 },
    { avBg: "#1A2B4A", initial: "M", name: "Mirjam K.", loc: "Den Haag",
      text: "Wespennest snel en veilig verwijderd. De medewerker was vriendelijk, legde alles goed uit en het probleem was direct opgelost. Top service!",
      date: "1 maand geleden", stars: 5 },
    { avBg: "#22C55E", initial: "K", name: "Koen B.", loc: "Rotterdam",
      text: "Ernstige overlast van kakkerlakken in mijn restaurant. ABC was de volgende ochtend aanwezig. Professioneel en mijn zaak kon open blijven. Aanrader!",
      date: "3 weken geleden", stars: 5 },
    { avBg: "#8B5CF6", initial: "B", name: "Bert J.", loc: "Delft",
      text: "Ratten in de kruipruimte opgelost inclusief afdichting. Ze kunnen niet meer terug. Eerlijke prijs en professionele aanpak.",
      date: "2 maanden geleden", stars: 5 },
    { avBg: "#F59E0B", initial: "D", name: "David S.", loc: "Dordrecht",
      text: "Bedwantsen volledig geëlimineerd. Nu eindelijk weer rustig slapen. Enorm blij met de snelheid en grondigheid van de aanpak!",
      date: "1 maand geleden", stars: 4 },
    { avBg: "#EF4444", initial: "R", name: "Ralph M.", loc: "Schiedam",
      text: "Snelle reactie, eerlijke prijs en effectieve behandeling. Mieren volledig verdwenen na één behandeling. ABC Ongedierte, ik raad ze iedereen aan!",
      date: "3 maanden geleden", stars: 5 },
  ];
  return (
    <section className="section section-bg" id="reviews">
      <div className="container">
        <div className="reviews-head">
          <div>
            <div className="section-tag">Klantbeoordelingen</div>
            <h2 style={{ fontSize: '2rem', fontWeight: 800, marginTop: 8, color: 'var(--navy)' }}>
              Wat onze klanten <em style={{ color: 'var(--orange)', fontStyle: 'normal' }}>zeggen</em>
            </h2>
          </div>
          <div className="g-score">
            <div className="g-score-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A10.997 10.997 0 0 0 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.997 10.997 0 0 0 1 12c0 1.77.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="g-num">{ratingStr}</span>
                <span className="g-stars">★★★★★</span>
              </div>
              <div className="g-lbl">op basis van {agg.total} Google reviews</div>
            </div>
          </div>
        </div>

        <div className="reviews-grid">
          {reviews.map((r, i) => (
            <div key={i} className={`rc fi fi-d${(i % 6) + 1}`}>
              <div className="rc-top">
                <div className="rc-av" style={{ background: r.avBg }}>{r.initial}</div>
                <div style={{ flex: 1 }}>
                  <div className="rc-name">{r.name}</div>
                  <div className="rc-loc"><IconPin width="11" height="11" />&nbsp;{r.loc}</div>
                </div>
              </div>
              <div className="rc-stars">
                {Array.from({ length: 5 }).map((_, k) => (
                  <span key={k} style={{ opacity: k < r.stars ? 1 : 0.25 }}>★</span>
                ))}
              </div>
              <p className="rc-text">"{r.text}"</p>
              <div className="rc-date">{r.date}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Werkgebied ──────────────────────────────────────────────────────
function Werkgebied() {
  // Group primary cities per province. Editorial flow-list — geen kaartjes,
  // wel ruimte om overzichtelijk te scannen op een hoofdpagina.
  const provinces = [
    { naam: "Zuid-Holland",  filter: (s) => s.provincie === "Zuid-Holland"  },
    { naam: "Noord-Holland", filter: (s) => s.provincie === "Noord-Holland" },
    { naam: "Utrecht",       filter: (s) => s.provincie === "Utrecht"       },
  ];
  const groups = provinces
    .map((p) => ({ naam: p.naam, cities: STEDEN.filter((s) => s.primary).filter(p.filter) }))
    .filter((g) => g.cities.length > 0);
  const totalCities = STEDEN.filter((s) => s.primary).length;

  return (
    <section className="section werkgebied-prov" id="gebieden">
      <div className="container">
        <div className="sh">
          <div className="section-tag">Werkgebied</div>
          <h2>Actief in heel <em>Zuid-Holland</em>, <em>Noord-Holland</em> &amp; <em>Utrecht</em></h2>
          <p>Vind de specialist voor jouw stad of gemeente. Wij zijn dagelijks actief in de hele Randstad.</p>
        </div>

        <div className="wgp-grid">
          {groups.map((g, i) => (
            <div key={g.naam} className="wgp-prov" data-prov={i}>
              <div className="wgp-prov-head">
                <div className="wgp-prov-titlewrap">
                  <h3 className="wgp-prov-name">{g.naam}</h3>
                  <span className="wgp-prov-meta">{g.cities.length} steden &amp; gemeenten</span>
                </div>
                <span className="wgp-prov-bar" aria-hidden="true"></span>
              </div>

              <ul className="wgp-city-list">
                {g.cities.map((s) => (
                  <li key={s.slug}>
                    <a href={url.stad(s.slug)} className="wgp-city">
                      <span className="wgp-city-name">{s.naam}</span>
                      <span className="wgp-city-arrow" aria-hidden="true">→</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <p className="wgp-total">
          Totaal <strong>{totalCities} steden &amp; gemeenten</strong> in ons werkgebied.
        </p>
      </div>
    </section>
  );
}

// ─── Contact / CTA ────────────────────────────────────────────────────
// Lead-intake via Web3Forms (zie src/submit-form.js). De aparte leads-app
// integratie (VITE_LEADS_API_URL) is uitgeschakeld tot die backend live
// staat, omdat localhost:3000 in productie 404 gaf en formulieren niet
// werkten. Switch terug door submitToLeadsApp() te re-activeren.

function ContactSection() {
  const [form, setForm] = useState({
    voornaam: "", achternaam: "", telefoon: "", email: "", postcode: "", ongedierte: "", omschrijving: ""
  });
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const upd = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setSubmitError(null);

    const naamVoluit = [form.voornaam, form.achternaam].filter(Boolean).join(' ').trim();
    const formNaam = `Contactformulier ${form.ongedierte || 'Algemeen'}`;
    const { ok, error } = await submitForm({
      subject: formNaam,
      formName: formNaam,
      fields: {
        Voornaam: form.voornaam,
        Achternaam: form.achternaam,
        Telefoon: form.telefoon,
        Email: form.email,
        Postcode: form.postcode,
        Ongedierte: form.ongedierte,
        Omschrijving: form.omschrijving,
      },
    });

    setSubmitting(false);
    if (ok) {
      setSent(true);
    } else {
      setSubmitError('Versturen mislukt. Bel ons direct op 085 500 50 50.');
      console.error('[ContactSection]', error);
    }
  };

  return (
    <section className="cta-section" id="contact">
      <div className="container">
        <div className="cta-inner">
          <div className="cta-text">
            <h2>Klaar om van je<br/><em>ongedierte af</em> te komen?</h2>
            <p>
              Neem nu contact op en ontvang binnen 24 uur een reactie.
              Spoedhulp ook 's avonds en in weekenden.
            </p>
            <div className="cta-btns">
              <a href="tel:0855005050" className="cta-bel">
                <IconPhone width="22" height="22" />
                <div>
                  <span className="cta-bel-sub">Bel ons direct</span>
                  <div>085 500 50 50</div>
                </div>
              </a>
              <a href="mailto:info@abcongedierte.nl" className="cta-email">
                <IconMail width="20" height="20" />
                <div>
                  <span className="cta-bel-sub">Stuur een e-mail</span>
                  <div>info@abcongedierte.nl</div>
                </div>
              </a>
            </div>
            <div className="cta-trust">
              <div className="cta-ti"><IconCheck width="12" height="12" />&nbsp;Reactie binnen 24 uur</div>
              <div className="cta-ti"><IconCheck width="12" height="12" />&nbsp;Vaste prijs vooraf</div>
              <div className="cta-ti"><IconCheck width="12" height="12" />&nbsp;Geen verplichtingen</div>
            </div>
          </div>

          <div className="form-box">
            {sent ? (
              <div style={{ textAlign: 'center', padding: '24px 0' }}>
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: 10,
                  padding: '10px 18px', borderRadius: 999,
                  background: '#E8F8EE', color: 'var(--green)',
                  fontWeight: 700, fontSize: 13
                }}>
                  <IconCheck /> AANVRAAG BINNEN
                </div>
                <h3 style={{ margin: '20px 0 10px', color: 'var(--navy)' }}>Bedankt! Wij bellen u terug.</h3>
                <p style={{ color: 'var(--text-muted)', fontSize: '.92rem' }}>
                  Binnen 24 uur op werkdagen. Spoed? Bel <strong style={{ color: 'var(--orange)' }}>085 500 50 50</strong>.
                </p>
                <button
                  type="button"
                  onClick={() => { setSent(false); setForm({ voornaam: "", achternaam: "", telefoon: "", email: "", postcode: "", ongedierte: "", omschrijving: "" }); }}
                  className="btn-verstuur" style={{ marginTop: 24 }}
                >Nog een aanvraag</button>
              </div>
            ) : (
              <>
                <h3>Offerte aanvragen</h3>
                <p>Vul in en wij nemen zo snel mogelijk contact op.</p>
                <form onSubmit={submit}>
                  <div className="form-row">
                    <div className="fg"><label>Voornaam *</label><input type="text" name="voornaam" value={form.voornaam} onChange={upd('voornaam')} placeholder="Jan" required /></div>
                    <div className="fg"><label>Achternaam</label><input type="text" name="achternaam" value={form.achternaam} onChange={upd('achternaam')} placeholder="de Vries" /></div>
                  </div>
                  <div className="fg"><label>Telefoonnummer *</label><input type="tel" name="telefoon" value={form.telefoon} onChange={upd('telefoon')} placeholder="06 12 34 56 78" required /></div>
                  <div className="fg"><label>E-mailadres *</label><input type="email" name="email" value={form.email} onChange={upd('email')} placeholder="jouw@email.nl" required /></div>
                  <div className="form-row">
                    <div className="fg"><label>Postcode</label><input type="text" name="postcode" value={form.postcode} onChange={upd('postcode')} placeholder="1234 AB" /></div>
                    <div className="fg"><label>Ongedierte *</label>
                      <select name="ongedierte" value={form.ongedierte} onChange={upd('ongedierte')} required>
                        <option value="">Selecteer...</option>
                        <option>Muizen</option><option>Ratten</option><option>Wespen</option><option>Bedwantsen</option><option>Mieren</option><option>Kakkerlakken</option><option>Spinnen</option><option>Rioolvliegjes</option><option>Vliegen</option><option>Vlooien</option><option>Zilvervisjes</option><option>Papiervisjes</option><option>Anders / weet ik niet</option>
                      </select>
                    </div>
                  </div>
                  <div className="fg"><label>Omschrijving</label><textarea name="omschrijving" value={form.omschrijving} onChange={upd('omschrijving')} placeholder="Kort omschrijven van het probleem..." /></div>
                  {submitError && (
                    <div style={{
                      padding: '10px 12px', marginBottom: 10,
                      background: '#FEF2F2', border: '1px solid #FCA5A5',
                      borderRadius: 8, color: '#991B1B', fontSize: 13,
                    }}>{submitError}</div>
                  )}
                  <button type="submit" className="btn-verstuur" disabled={submitting} style={submitting ? { opacity: 0.6, cursor: 'wait' } : undefined}>
                    {submitting ? 'Bezig met versturen…' : '✉ Verstuur aanvraag, gratis & vrijblijvend'}
                  </button>
                  <p className="form-note">✓ 100% gratis en vrijblijvend, reactie binnen 24 uur</p>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export function HomePage() {
  // ── SEO: homepage meta + breadcrumb + bedrijfs-aggregateRating.
  // 2026-05-29: aggregateRating staat ALLEEN op homepage + /reviews/ (niet op
  // stad/plaag/combo). Google's review-snippet guidelines: rating moet matchen
  // met page-content. Homepage = bedrijf als geheel = legitieme plek.
  useSEO({
    jsonLd: [
      buildBreadcrumb([{ name: "Home", url: "/" }]),
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        "@id": "https://www.abcongedierte.nl/#organization",
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": String(REVIEWS.rating),
          "reviewCount": String(REVIEWS.total),
          "bestRating": "5",
          "worstRating": "1"
        }
      }
    ],
    title: "Ongediertebestrijding Zuid-Holland, Noord-Holland & Utrecht",
    description: `Last van ongedierte? ABC helpt particulier en zakelijk in ZH, NH en Utrecht. Spoeddienst 24/7, gecertificeerde partners, ${REVIEWS.ratingStr}/5 op ${REVIEWS.total} reviews.`,
    path: "/",
  });

  useEffect(() => {
    // fade-in observer
    const io = new IntersectionObserver(entries => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('in');
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.1 });
    document.querySelectorAll('.fi').forEach(el => io.observe(el));

    // Wink trigger removed — pests no longer animate on click.
    const cleaners = [];

    // Deep-link anchor (URL like /path#contact): browser scrolls before React
    // mounts, so we re-scroll once the element is rendered.
    const m = window.location.hash.match(/^#(\w+)$/);
    if (m && m[1]) {
      setTimeout(() => {
        const el = document.getElementById(m[1]);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
    return () => {
      io.disconnect();
      cleaners.forEach(fn => fn());
    };
  }, []);

  return (
    <div className="page-anim">
      <Hero />
      <Stats />
      <Plagen />
      <Werkwijze />
      <USPs />
      <Reviews />
      <Werkgebied />
      <ContactSection />
    </div>
  );
}

